import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import sessionService, {
    Session,
    CreateSessionData,
    UpdateSessionData,
    CreateActivityData,
    SessionType,
} from '@/services/sessionApi';
import {
    // Session CRUD
    fetchSessionsRequest,
    fetchSessionsSuccess,
    fetchSessionsFailure,
    fetchSessionByIdRequest,
    fetchSessionByIdSuccess,
    fetchSessionByIdFailure,
    createSessionRequest,
    createSessionSuccess,
    createSessionFailure,
    updateSessionRequest,
    updateSessionSuccess,
    updateSessionFailure,
    deleteSessionRequest,
    deleteSessionSuccess,
    deleteSessionFailure,

    // Session Control
    pauseSessionRequest,
    pauseSessionSuccess,
    pauseSessionFailure,
    resumeSessionRequest,
    resumeSessionSuccess,
    resumeSessionFailure,
    completeSessionRequest,
    completeSessionSuccess,
    completeSessionFailure,
    abandonSessionRequest,
    abandonSessionSuccess,
    abandonSessionFailure,

    // Active Sessions
    fetchActiveSessionsRequest,
    fetchActiveSessionsSuccess,
    fetchActiveSessionsFailure,

    // Activities
    fetchSessionActivitiesRequest,
    fetchSessionActivitiesSuccess,
    fetchSessionActivitiesFailure,
    createSessionActivityRequest,
    createSessionActivitySuccess,
    createSessionActivityFailure,

    // Bookmarks
    fetchSessionBookmarksRequest,
    fetchSessionBookmarksSuccess,
    fetchSessionBookmarksFailure,
    createSessionBookmarkRequest,
    createSessionBookmarkSuccess,
    createSessionBookmarkFailure,

    // Templates
    fetchSessionTemplatesRequest,
    fetchSessionTemplatesSuccess,
    fetchSessionTemplatesFailure,
    createSessionFromTemplateRequest,
    createSessionFromTemplateSuccess,
    createSessionFromTemplateFailure,

    // Stats
    fetchSessionStatsRequest,
    fetchSessionStatsSuccess,
    fetchSessionStatsFailure,

    // Filters
    fetchSessionsByTypeRequest,
    fetchSessionsByTypeSuccess,
    fetchSessionsByTypeFailure,
    fetchSessionsByDateRangeRequest,
    fetchSessionsByDateRangeSuccess,
    fetchSessionsByDateRangeFailure,

    // Analytics
    fetchSessionAnalyticsRequest,
    fetchSessionAnalyticsSuccess,
    fetchSessionAnalyticsFailure,
} from '../slices/sessionSlice';

// Worker Sagas

// Session CRUD Workers
function* fetchSessionsWorker(): SagaIterator {
    try {
        const sessions = yield call(sessionService.getSessions);
        yield put(fetchSessionsSuccess(sessions));
    } catch (error: any) {
        yield put(fetchSessionsFailure(error.response?.data?.message || 'Failed to fetch sessions'));
    }
}

function* fetchSessionByIdWorker(action: PayloadAction<number>): SagaIterator {
    try {
        const session = yield call(sessionService.getSession, action.payload);
        yield put(fetchSessionByIdSuccess(session));
    } catch (error: any) {
        yield put(fetchSessionByIdFailure(error.response?.data?.message || 'Failed to fetch session'));
    }
}

function* createSessionWorker(action: PayloadAction<CreateSessionData>): SagaIterator {
    try {
        const session = yield call(sessionService.createSession, action.payload);
        yield put(createSessionSuccess(session));
    } catch (error: any) {
        yield put(createSessionFailure(error.response?.data?.message || 'Failed to create session'));
    }
}

function* updateSessionWorker(action: PayloadAction<{ sessionId: number; data: UpdateSessionData }>): SagaIterator {
    try {
        const { sessionId, data } = action.payload;
        const session = yield call(sessionService.updateSession, sessionId, data);
        yield put(updateSessionSuccess(session));
    } catch (error: any) {
        yield put(updateSessionFailure(error.response?.data?.message || 'Failed to update session'));
    }
}

function* deleteSessionWorker(action: PayloadAction<number>): SagaIterator {
    try {
        yield call(sessionService.deleteSession, action.payload);
        yield put(deleteSessionSuccess(action.payload));
    } catch (error: any) {
        yield put(deleteSessionFailure(error.response?.data?.message || 'Failed to delete session'));
    }
}

// Session Control Workers
function* pauseSessionWorker(action: PayloadAction<number>): SagaIterator {
    try {
        const result = yield call(sessionService.pauseSession, action.payload);
        yield put(pauseSessionSuccess({ sessionId: action.payload, status: result.status }));
    } catch (error: any) {
        yield put(pauseSessionFailure(error.response?.data?.message || 'Failed to pause session'));
    }
}

function* resumeSessionWorker(action: PayloadAction<number>): SagaIterator {
    try {
        const result = yield call(sessionService.resumeSession, action.payload);
        yield put(resumeSessionSuccess({ sessionId: action.payload, status: result.status }));
    } catch (error: any) {
        yield put(resumeSessionFailure(error.response?.data?.message || 'Failed to resume session'));
    }
}

function* completeSessionWorker(action: PayloadAction<{ sessionId: number; finalScore?: number }>): SagaIterator {
    try {
        const { sessionId, finalScore } = action.payload;
        const session = yield call(sessionService.completeSession, sessionId, finalScore);
        yield put(completeSessionSuccess(session));
    } catch (error: any) {
        yield put(completeSessionFailure(error.response?.data?.message || 'Failed to complete session'));
    }
}

function* abandonSessionWorker(action: PayloadAction<number>): SagaIterator {
    try {
        const result = yield call(sessionService.abandonSession, action.payload);
        yield put(abandonSessionSuccess({ sessionId: action.payload, status: result.status }));
    } catch (error: any) {
        yield put(abandonSessionFailure(error.response?.data?.message || 'Failed to abandon session'));
    }
}

// Active Sessions Worker
function* fetchActiveSessionsWorker(): SagaIterator {
    try {
        const sessions = yield call(sessionService.getActiveSessions);
        yield put(fetchActiveSessionsSuccess(sessions));
    } catch (error: any) {
        yield put(fetchActiveSessionsFailure(error.response?.data?.message || 'Failed to fetch active sessions'));
    }
}

// Session Activities Workers
function* fetchSessionActivitiesWorker(action: PayloadAction<number>): SagaIterator {
    try {
        const activities = yield call(sessionService.getSessionActivities, action.payload);
        yield put(fetchSessionActivitiesSuccess(activities));
    } catch (error: any) {
        yield put(fetchSessionActivitiesFailure(error.response?.data?.message || 'Failed to fetch session activities'));
    }
}

function* createSessionActivityWorker(
    action: PayloadAction<{ sessionId: number; data: CreateActivityData }>,
): SagaIterator {
    try {
        const { sessionId, data } = action.payload;
        const activity = yield call(sessionService.createSessionActivity, sessionId, data);
        yield put(createSessionActivitySuccess(activity));
    } catch (error: any) {
        yield put(createSessionActivityFailure(error.response?.data?.message || 'Failed to create session activity'));
    }
}

// Session Bookmarks Workers
function* fetchSessionBookmarksWorker(action: PayloadAction<number>): SagaIterator {
    try {
        const bookmarks = yield call(sessionService.getSessionBookmarks, action.payload);
        yield put(fetchSessionBookmarksSuccess(bookmarks));
    } catch (error: any) {
        yield put(fetchSessionBookmarksFailure(error.response?.data?.message || 'Failed to fetch session bookmarks'));
    }
}

function* createSessionBookmarkWorker(
    action: PayloadAction<{
        sessionId: number;
        data: {
            title: string;
            description?: string;
            activity_sequence: number;
            bookmark_data?: Record<string, any>;
        };
    }>,
): SagaIterator {
    try {
        const { sessionId, data } = action.payload;
        const bookmark = yield call(sessionService.createSessionBookmark, sessionId, data);
        yield put(createSessionBookmarkSuccess(bookmark));
    } catch (error: any) {
        yield put(createSessionBookmarkFailure(error.response?.data?.message || 'Failed to create session bookmark'));
    }
}

// Session Templates Workers
function* fetchSessionTemplatesWorker(): SagaIterator {
    try {
        const templates = yield call(sessionService.getSessionTemplates);
        yield put(fetchSessionTemplatesSuccess(templates));
    } catch (error: any) {
        yield put(fetchSessionTemplatesFailure(error.response?.data?.message || 'Failed to fetch session templates'));
    }
}

function* createSessionFromTemplateWorker(
    action: PayloadAction<{
        templateId: number;
        data: {
            title?: string;
            session_data?: Record<string, any>;
            content_type?: number;
            object_id?: number;
        };
    }>,
): SagaIterator {
    try {
        const { templateId, data } = action.payload;
        const session = yield call(sessionService.createSessionFromTemplate, templateId, data);
        yield put(createSessionFromTemplateSuccess(session));
    } catch (error: any) {
        yield put(
            createSessionFromTemplateFailure(error.response?.data?.message || 'Failed to create session from template'),
        );
    }
}

// Session Stats Worker
function* fetchSessionStatsWorker(): SagaIterator {
    try {
        const stats = yield call(sessionService.getSessionStats);
        yield put(fetchSessionStatsSuccess(stats));
    } catch (error: any) {
        yield put(fetchSessionStatsFailure(error.response?.data?.message || 'Failed to fetch session stats'));
    }
}

// Filter Workers
function* fetchSessionsByTypeWorker(action: PayloadAction<keyof SessionType>): SagaIterator {
    try {
        const sessions = yield call(sessionService.getSessionsByType, action.payload);
        yield put(fetchSessionsByTypeSuccess(sessions));
    } catch (error: any) {
        yield put(fetchSessionsByTypeFailure(error.response?.data?.message || 'Failed to fetch sessions by type'));
    }
}

function* fetchSessionsByDateRangeWorker(action: PayloadAction<{ startDate: string; endDate: string }>): SagaIterator {
    try {
        const { startDate, endDate } = action.payload;
        const sessions = yield call(sessionService.getSessionsByDateRange, startDate, endDate);
        yield put(fetchSessionsByDateRangeSuccess(sessions));
    } catch (error: any) {
        yield put(
            fetchSessionsByDateRangeFailure(error.response?.data?.message || 'Failed to fetch sessions by date range'),
        );
    }
}

// Analytics Worker
function* fetchSessionAnalyticsWorker(): SagaIterator {
    try {
        const analytics = yield call(sessionService.getSessionAnalytics);
        yield put(fetchSessionAnalyticsSuccess(analytics));
    } catch (error: any) {
        yield put(fetchSessionAnalyticsFailure(error.response?.data?.message || 'Failed to fetch session analytics'));
    }
}

// Watcher Sagas
function* watchSessions(): SagaIterator {
    // Session CRUD
    yield takeLatest(fetchSessionsRequest.type, fetchSessionsWorker);
    yield takeLatest(fetchSessionByIdRequest.type, fetchSessionByIdWorker);
    yield takeLatest(createSessionRequest.type, createSessionWorker);
    yield takeLatest(updateSessionRequest.type, updateSessionWorker);
    yield takeLatest(deleteSessionRequest.type, deleteSessionWorker);

    // Session Control
    yield takeLatest(pauseSessionRequest.type, pauseSessionWorker);
    yield takeLatest(resumeSessionRequest.type, resumeSessionWorker);
    yield takeLatest(completeSessionRequest.type, completeSessionWorker);
    yield takeLatest(abandonSessionRequest.type, abandonSessionWorker);

    // Active Sessions
    yield takeLatest(fetchActiveSessionsRequest.type, fetchActiveSessionsWorker);

    // Activities
    yield takeLatest(fetchSessionActivitiesRequest.type, fetchSessionActivitiesWorker);
    yield takeLatest(createSessionActivityRequest.type, createSessionActivityWorker);

    // Bookmarks
    yield takeLatest(fetchSessionBookmarksRequest.type, fetchSessionBookmarksWorker);
    yield takeLatest(createSessionBookmarkRequest.type, createSessionBookmarkWorker);

    // Templates
    yield takeLatest(fetchSessionTemplatesRequest.type, fetchSessionTemplatesWorker);
    yield takeLatest(createSessionFromTemplateRequest.type, createSessionFromTemplateWorker);

    // Stats
    yield takeLatest(fetchSessionStatsRequest.type, fetchSessionStatsWorker);

    // Filters
    yield takeLatest(fetchSessionsByTypeRequest.type, fetchSessionsByTypeWorker);
    yield takeLatest(fetchSessionsByDateRangeRequest.type, fetchSessionsByDateRangeWorker);

    // Analytics
    yield takeLatest(fetchSessionAnalyticsRequest.type, fetchSessionAnalyticsWorker);
}

export default watchSessions;
